import React, { useRef, useState } from 'react';
import { Card, CardHead, RoundButton, SmallPill, DeviceSwitch, RING_ON_CARD, T } from '../fields/ui';
import LoadingSpinner from '../../LoadingSpinner';
import useImageRatios from '../../../hooks/useImageRatios';
import { geom, tilePad, clamp, overflowFor, DESKTOP_LAYOUTS, MOBILE_LAYOUTS, MOBILE_RATIO } from '../../../utils/portalCover';
import { MAX_PHOTOS } from '../../../utils/portalPageModel';

/*
 * PHOTOS + LAYOUT — design_handoff_portal_profile_editor "Tab: PHOTOS + LAYOUT".
 *
 * PHOTOS: the tray (1:1 thumbs, radius 16; photo 1 wears a 2px white ring because it leads the
 * cover; a number badge; PROFILE on the avatar photo). Pointer-drag on a thumb sets its focal
 * point — the image follows the finger: Δ / overflow × 100, from the photo's natural aspect
 * against the box. ◄ ► reorder, × remove (not at one photo), SET AS PROFILE. + ADD opens the
 * gallery upload while there are fewer than nine.
 *
 * PROFILE PHOTO: a 180px square dragged to frame the face; its own focal point (ax, ay) so the
 * avatar crop and the cover crop are independent. Live 76 / 40 / 28px previews. RESET FRAMING.
 *
 * COVER LAYOUT: DESKTOP | MOBILE (shared with the preview), option cards with minis drawn from
 * the real photos through the same geometry, summary chips, RESET PHOTO FRAMING.
 */

const X = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>;

/**
 * Start a focal drag on `e.currentTarget`. `start` is the focal point when the pointer went down;
 * `onMove(fx, fy)` gets the clamped, rounded point as the pointer moves.
 */
export function startFocalDrag(e, ratio, start, onMove) {
  e.preventDefault();
  const rect = e.currentTarget.getBoundingClientRect();
  const { ox, oy } = overflowFor(ratio, rect.width, rect.height);
  const s = { x: e.clientX, y: e.clientY, fx: start.fx, fy: start.fy };
  const move = (ev) => {
    const fx = ox > 1 ? clamp(s.fx - ((ev.clientX - s.x) / ox) * 100, 0, 100) : s.fx;
    const fy = oy > 1 ? clamp(s.fy - ((ev.clientY - s.y) / oy) * 100, 0, 100) : s.fy;
    onMove(Math.round(fx), Math.round(fy));
  };
  const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
}

function Framed({ src, fx, fy, size, radius, label, round }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <span style={{ width: size, height: size, borderRadius: round ? '50%' : radius, overflow: 'hidden', background: '#000', display: 'block' }}>
        {src && <img src={src} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${fx}% ${fy}%`, display: 'block' }} />}
      </span>
      <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '.14em', color: '#8A8A8A' }}>{label}</span>
    </span>
  );
}

/** A layout option's mini cover: the real photos through `geom`, text bars where the words go. */
function Mini({ layoutKey, mobile, photos }) {
  const n = photos.length;
  const g = geom(layoutKey, n, mobile);
  const bar = (w, h, bg) => <span style={{ display: 'block', width: w, height: h, borderRadius: 2, background: bg }} />;
  return (
    <>
      <span style={{ position: 'relative', display: 'block', width: '100%', aspectRatio: mobile ? String(1 / MOBILE_RATIO[layoutKey]) : '2.35', borderRadius: 10, overflow: 'hidden', background: '#1A1A1A' }}>
        {g.tiles.filter((t) => photos[t.i]).map((t) => {
          const p = photos[t.i];
          return (
            <span key={`${t.i}-${t.x}`} style={{ position: 'absolute', left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%`, height: `${t.h}%`, padding: tilePad(t, 2), boxSizing: 'border-box' }}>
              <img src={p.src} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${p.fx}% ${p.fy}%`, display: 'block' }} />
            </span>
          );
        })}
        {!mobile && g.fade && <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,#000 0%,rgba(0,0,0,.85) 36%,rgba(0,0,0,0) 70%)' }} />}
        {!mobile && (
          <span style={{ position: 'absolute', left: '7%', top: '20%', width: `${g.textW * 0.8}%`, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {bar('72%', 7, '#fff')}{bar('38%', 4, '#FF66C4')}{bar('60%', 4, '#555')}
          </span>
        )}
        {mobile && layoutKey === 'backdrop' && (
          <>
            <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(0,0,0,0) 40%,rgba(0,0,0,.92) 88%)' }} />
            <span style={{ position: 'absolute', left: '9%', right: '9%', bottom: '9%', display: 'flex', flexDirection: 'column', gap: 4 }}>{bar('70%', 7, '#fff')}{bar('36%', 4, '#FF66C4')}</span>
          </>
        )}
        {mobile && layoutKey === 'carousel' && (
          <span style={{ position: 'absolute', left: '50%', bottom: '8%', transform: 'translateX(-50%)', display: 'flex', gap: 3 }}>
            {bar(10, 4, '#fff')}{bar(4, 4, 'rgba(255,255,255,.5)')}{bar(4, 4, 'rgba(255,255,255,.5)')}
          </span>
        )}
      </span>
      {mobile && layoutKey !== 'backdrop' && <span style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 2px' }}>{bar('62%', 6, '#fff')}{bar('34%', 4, '#FF66C4')}</span>}
    </>
  );
}

export default function PhotosTab({ d, upd, phone, device, setDevice, onUpload, uploading }) {
  const fileRef = useRef(null);
  const [uploadError, setUploadError] = useState(null);
  const photos = d.photos;
  const n = photos.length;
  const ratios = useImageRatios(photos.map((p) => p.src));
  const av = photos.find((p) => p.id === d.avatar) || photos[0] || null;
  const mobile = device === 'mobile';

  const move = (i, dd) => upd((s) => {
    const a = [...s.photos];
    const j = i + dd;
    if (j < 0 || j >= a.length) return {};
    [a[i], a[j]] = [a[j], a[i]];
    return { photos: a };
  });
  const remove = (p) => upd((s) => {
    if (s.photos.length <= 1) return {};
    const rest = s.photos.filter((q) => q.id !== p.id);
    const keep = rest.find((q) => q.id === s.avatar);
    return keep ? { photos: rest } : { photos: rest, avatar: rest[0].id, ax: rest[0].fx, ay: rest[0].fy };
  });
  const setAvatar = (p) => upd({ avatar: p.id, ax: p.fx, ay: p.fy });
  const pick = async (file) => {
    if (!file) return;
    setUploadError(null);
    const err = await onUpload(file);
    if (err) setUploadError(err);
  };

  const metaSet = mobile ? MOBILE_LAYOUTS : DESKTOP_LAYOUTS;
  const curLayout = mobile ? d.layoutM : d.layoutD;

  return (
    <>
      <Card phone={phone} data-testid="photos-card" style={{ gap: 16 }}>
        <CardHead title="PHOTOS" help="Order sets where each photo lands. Drag a photo to choose what stays in frame." right={<span style={{ padding: '5px 10px', borderRadius: 999, background: '#1F1F1F', fontSize: 9, fontWeight: 700, letterSpacing: '.14em' }}>{n} OF {MAX_PHOTOS}</span>} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(118px,1fr))', gap: 12 }}>
          {photos.map((p, i) => {
            const isAv = av && p.id === av.id;
            return (
              <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }} data-testid="tray-photo">
                <div
                  onPointerDown={(e) => startFocalDrag(e, ratios[p.src], { fx: p.fx, fy: p.fy }, (fx, fy) => upd((s) => ({ photos: s.photos.map((q) => (q.id === p.id ? { ...q, fx, fy } : q)) })))}
                  style={{ position: 'relative', aspectRatio: '1', borderRadius: 16, overflow: 'hidden', background: '#000', boxShadow: i === 0 ? 'inset 0 0 0 2px #fff' : 'none', cursor: 'grab', touchAction: 'none' }}
                  aria-label={`Photo ${i + 1}: drag to frame`}
                >
                  <img src={p.src} alt="" referrerPolicy="no-referrer" draggable="false" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${p.fx}% ${p.fy}%`, display: 'block', pointerEvents: 'none', userSelect: 'none' }} />
                  <span style={{ position: 'absolute', left: 6, top: 6, minWidth: 22, height: 22, padding: '0 6px', boxSizing: 'border-box', borderRadius: 999, background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, pointerEvents: 'none' }}>{i + 1}</span>
                  {isAv && <span style={{ position: 'absolute', right: 6, top: 6, padding: '4px 8px', borderRadius: 999, background: '#FF00AA', color: '#fff', fontSize: 8, fontWeight: 800, letterSpacing: '.14em', pointerEvents: 'none' }}>PROFILE</span>}
                </div>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <RoundButton onClick={() => move(i, -1)} dim={i === 0} aria-label={`Move photo ${i + 1} earlier`}>◄</RoundButton>
                  <RoundButton onClick={() => move(i, 1)} dim={i === n - 1} aria-label={`Move photo ${i + 1} later`}>►</RoundButton>
                  <span style={{ flex: 1 }} />
                  <RoundButton onClick={() => remove(p)} dim={n === 1} disabled={n === 1} color="#FF6B6B" aria-label={`Remove photo ${i + 1}`}>{X}</RoundButton>
                </span>
                <button type="button" onClick={() => setAvatar(p)} className="pk-btn" style={{ height: 28, background: isAv ? '#FF00AA' : '#1F1F1F', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '.12em' }}>{isAv ? 'PROFILE PHOTO' : 'SET AS PROFILE'}</button>
              </div>
            );
          })}
          {n < MAX_PHOTOS && (
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="pe-add" aria-busy={uploading} style={{ aspectRatio: '1', border: '2px dashed #333', borderRadius: 16, background: '#000', color: '#8A8A8A', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', transition: 'border-color .2s, color .2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {uploading ? <LoadingSpinner size="md" /> : '+ ADD'}
            </button>
          )}
        </div>
        {uploadError && <span role="alert" style={{ fontSize: 11, color: '#FF6B6B' }}>{uploadError}</span>}
        <input ref={fileRef} type="file" accept="image/*" data-testid="photo-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; }} style={{ display: 'none' }} />
      </Card>

      <Card phone={phone} data-testid="avatar-card" style={{ gap: 16 }}>
        <CardHead title="PROFILE PHOTO" help="Shows in the header, on People cards and on guestlists. Pick it from your photos above, then drag to frame your face." />
        {av ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 20 }}>
            <div
              onPointerDown={(e) => startFocalDrag(e, ratios[av.src], { fx: d.ax, fy: d.ay }, (ax, ay) => upd({ ax, ay }))}
              aria-label="Profile photo: drag to frame your face"
              data-testid="avatar-frame"
              style={{ flexShrink: 0, width: 180, height: 180, borderRadius: 28, overflow: 'hidden', background: '#000', cursor: 'grab', touchAction: 'none' }}
            >
              <img src={av.src} alt="" referrerPolicy="no-referrer" draggable="false" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${d.ax}% ${d.ay}%`, display: 'block', pointerEvents: 'none', userSelect: 'none' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
              <Framed src={av.src} fx={d.ax} fy={d.ay} size={76} radius={18} label="PEOPLE CARD" />
              <Framed src={av.src} fx={d.ax} fy={d.ay} size={40} round label="HEADER" />
              <Framed src={av.src} fx={d.ax} fy={d.ay} size={28} round label="GUESTLIST" />
            </div>
            <SmallPill height={38} onClick={() => upd({ ax: av.fx ?? 50, ay: av.fy ?? 50 })}>RESET FRAMING</SmallPill>
          </div>
        ) : (
          <span style={T.help}>Add a photo above to set your profile photo.</span>
        )}
      </Card>

      <Card phone={phone} data-testid="layout-card" style={{ gap: 16 }}>
        <CardHead title="COVER LAYOUT" help="Desktop and mobile are set separately. Phones and small tablets use the mobile layout." right={<DeviceSwitch value={device} onChange={setDevice} />} />
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(auto-fill,minmax(140px,1fr))' : 'repeat(auto-fill,minmax(200px,1fr))', gap: 10, alignItems: 'start' }}>
          {Object.keys(metaSet).map((k) => {
            const isOn = k === curLayout;
            return (
              <button key={k} type="button" aria-pressed={isOn} onClick={() => upd(mobile ? { layoutM: k } : { layoutD: k })} data-testid={`layout-${mobile ? 'm' : 'd'}-${k}`} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10, border: '2px solid transparent', borderRadius: 20, background: isOn ? RING_ON_CARD : '#000', color: '#fff', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'background-color .2s', minWidth: 0 }}>
                <Mini layoutKey={k} mobile={mobile} photos={photos} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 4px 2px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{metaSet[k][0]}</span>
                    {isOn && <span style={{ padding: '2px 7px', borderRadius: 999, background: '#22ECFB', color: '#000', fontSize: 7, fontWeight: 800, letterSpacing: '.14em' }}>IN USE</span>}
                  </span>
                  <span style={{ fontSize: 10, lineHeight: 1.5, color: '#8A8A8A' }}>{metaSet[k][1]}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          {[`DESKTOP · ${DESKTOP_LAYOUTS[d.layoutD][0]}`, `MOBILE · ${MOBILE_LAYOUTS[d.layoutM][0]}`].map((p) => (
            <span key={p} style={{ padding: '6px 12px', borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A', fontSize: 9, fontWeight: 700, letterSpacing: '.14em' }}>{p}</span>
          ))}
          <span style={{ flex: 1 }} />
          <SmallPill onClick={() => upd((s) => ({ photos: s.photos.map((p) => ({ ...p, fx: 50, fy: 50 })) }))}>RESET PHOTO FRAMING</SmallPill>
        </div>
      </Card>
    </>
  );
}
